import { assertNumber, assertMinimum, assertMaximum } from '../../validators';
import type { CommonAttributeSettings, CommonMetadataKeys } from './common';
import { createAttributeSetter, DEFAULT_ATTRIBUTE_SETTINGS, defineMetadata } from './common';

type NumberAttributeSettings = CommonAttributeSettings & {
    min: number;
    max: number;
};

export type NumberMetadataKeys = keyof NumberAttributeSettings | CommonMetadataKeys;
export const NUMBER_ATTRIBUTE_TYPE = 'numberAttributeType';
const DEFAULT_SETTINGS: NumberAttributeSettings = {
    ...DEFAULT_ATTRIBUTE_SETTINGS,
    min: -Infinity,
    max: Infinity,
    animatable: true,
};

/**
 * Creates an attribute decorator used to describe a numeric property. In addition to the shared
 * attribute settings, the number decorator allows validation to enforce a minimum and a maximum
 * value.
 */
export function NumberAttribute(settings: Partial<NumberAttributeSettings> = {}) {
    const resolvedSettings: NumberAttributeSettings = { ...DEFAULT_SETTINGS, ...settings };
    const validator = (value: number) => {
        assertNumber(value);
        assertMinimum(value, resolvedSettings.min);
        assertMaximum(value, resolvedSettings.max);
    };
    return function <This>(
        { get, set }: ClassAccessorDecoratorTarget<This, number>,
        context: ClassAccessorDecoratorContext,
    ): ClassAccessorDecoratorResult<This, number> {
        defineMetadata(
            resolvedSettings,
            NUMBER_ATTRIBUTE_TYPE,
            context,
            get as (this: unknown) => unknown,
        );

        return {
            get: function (this: This) {
                return get.call(this);
            },
            set: createAttributeSetter(
                validator,
                get as (this: object) => number,
                set as (this: object, value: number) => void,
            ) as (this: This, value: number) => void,
        };
    };
}
